import { ExperienceBaseSchema, ExperienceType } from "@acme/db/schema";
import { useAppForm } from "@acme/ui/form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { toast } from "sonner";
import { z } from "zod/v4";
import { ExperiencesForm } from "~/components/experiences/form";
import { queryKeys } from "~/lib/query-keys";
import { $createExperience } from "~/lib/server/experience";

export const Route = createFileRoute("/(dashboard)/experiences/create")({
  component: ExperiencesCreatePage,
  head: () => ({
    meta: [{ title: "Create Experience | Dashboard" }],
  }),
});

function ExperiencesCreatePage() {
  const router = useRouter();
  const queryClient = useQueryClient();

  const createExperienceMutation = useMutation({
    mutationFn: (data: z.infer<typeof ExperienceBaseSchema>) =>
      $createExperience({ data }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.experience.all,
      });
      toast.success("Experience created successfully");
      form.reset();
      router.navigate({ to: "/experiences" });
    },
    onError: (_error) => {
      toast.error("Failed to create experience");
    },
  });

  const handleFormSubmit = (data: z.infer<typeof ExperienceBaseSchema>) => {
    createExperienceMutation.mutate(data);
  };

  const form = useAppForm({
    defaultValues: {
      title: "",
      institution: "",
      description: "",
      thumbnail: "",
      startDate: "",
      endDate: "",
      url: "",
      type: ExperienceType.WORK,
      isDraft: false,
      isOnGoing: false,
    },
    validators: {
      onChange: ExperienceBaseSchema,
    },
    onSubmit: ({ value }) => {
      handleFormSubmit(value);
    },
  });

  return (
    <>
      <div className="mb-2 flex flex-wrap items-center justify-between space-y-2">
        <div>
          <h2 className="font-bold text-2xl tracking-tight">
            Create Experience
          </h2>
          <p className="text-muted-foreground">Create a new experience here.</p>
        </div>
      </div>
      <div className="py-4">
        <form.AppForm>
          <form
            className="space-y-8"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <ExperiencesForm experience={undefined} form={form} />
          </form>
        </form.AppForm>
      </div>
    </>
  );
}
